import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * The optimistic-update-and-rollback substrate.
 *
 * `FE-04.2` claimed this existed before it did; this file is the thing that
 * was missing. It owns the whole lifecycle of an optimistic TanStack Query
 * mutation so a caller cannot get any one step half right:
 *
 *  1. **cancel** any in-flight fetch for the affected key(s), so a refetch
 *     that settles mid-mutation cannot clobber the optimistic write with data
 *     that predates it;
 *  2. **snapshot** the cache exactly as it stood before, key by key —
 *     including the case where a key had no entry at all, which is not the
 *     same thing as an entry holding `undefined`;
 *  3. **write** the optimistic value into every affected key;
 *  4. on error, **restore** each key to its snapshot — putting back a value
 *     where one existed, and *removing the entry* where none did, because
 *     `queryClient.setQueryData(key, undefined)` is a documented no-op in
 *     TanStack Query v5 (a functional update that resolves to `undefined`
 *     causes `setQueryData` to return early and leave the cache untouched) —
 *     handing it `undefined` would leave the optimistic value stuck in the
 *     cache rather than reverting it;
 *  5. on settle (success or failure), **invalidate** every affected key so
 *     the server's own answer — fetched fresh — is what the UI ends up
 *     trusting, matching the `staleTime: 30_000` / `refetchOnWindowFocus`
 *     defaults `main.tsx` already sets: a board is collaborative, so cache
 *     truth is expected to lose to a refetch, not to linger.
 *
 * This module is deliberately inert: it does not know what a card, a list or
 * a board is, does not call `fetch`, and does not render. It is generic over
 * TanStack's own {@link QueryKey}, not this app's query-key factory — that
 * factory (`FE-04.13`) is landing in a sibling change at the same time this
 * one is, and importing it here would create a merge collision over nothing
 * this file needs: the caller already knows which keys it is touching.
 *
 * @example
 * ⚠ **CORRECTED IN CHUNK `C3`, AND THE ATTRIBUTION WAS THE WRONG WAY ROUND.**
 * This example was written as `FE-12.3`'s literal shape. It is not:
 * `FE-12.3` drags a card **within one board**, where both lists live inside
 * the same `boardContents` payload, so it is **one key and one updater**
 * (`C3-BRIEF.md` §5.1). `queryKeys.lists.cards(listId)` is never written by
 * the board face at all — no socket frame in this product carries a `listId`,
 * so `eventBlastRadius` can never reach that key.
 *
 * The two-key shape below is real and is still worth keeping — it is
 * **`FE-21.1`'s** (cross-board move, `FR-BOARD-026`, chunk `G2`), where the
 * source and destination genuinely are two different cached payloads. Read it
 * as that, and do not reach for it when moving a card between two lists of one
 * board.
 *
 * Two keys, one `updater` that branches on which key it is currently asked to
 * rewrite:
 *
 * ```ts
 * interface MoveCardVariables {
 *   cardId: string;
 *   fromListKey: QueryKey; // e.g. queryKeys.lists.cards(fromListId)
 *   toListKey: QueryKey;   // e.g. queryKeys.lists.cards(toListId)
 * }
 *
 * const queryClient = useQueryClient();
 *
 * const moveCard = useMutation({
 *   mutationFn: (variables: MoveCardVariables) => api.moveCard(variables),
 *   ...optimisticMutation<MoveCardVariables, CardListPage>({
 *     queryClient,
 *     keys: (variables) => [variables.fromListKey, variables.toListKey],
 *     updater: (previous, variables, key) => {
 *       if (key === variables.fromListKey) {
 *         return removeCard(previous, variables.cardId);
 *       }
 *       return insertCard(previous, variables.cardId);
 *     },
 *   }),
 * });
 * ```
 *
 * `moveCard`'s own resolved type (whatever `api.moveCard` returns) and its
 * error type are unrelated to `CardListPage`, the cache-entry shape above —
 * see "WHY `unknown`" below for why that is safe to spread into `useMutation`
 * as-is.
 *
 * FUTURE CALLERS (do not build these here):
 *  - **`FE-12.3`** (dragging a card between lists) was the first caller, and
 *    it landed in chunk `C3` — as **one** key, `queryKeys.boards.contents`,
 *    not as the two-key example above. See the correction there.
 *  - **`FE-18.1`** (reconciling a live socket delta against optimistic local
 *    state) is the last caller, nine chunks after `FE-12.3`. It is *not*
 *    implemented here: reconciling a server-pushed delta against whatever
 *    optimistic value is currently sitting in cache is a different problem
 *    from this one (there is no local mutation in flight to snapshot around,
 *    and the delta may need to merge with, rather than replace, the current
 *    entry). What this module guarantees FE-18.1 can rely on is narrower and
 *    load-bearing: once a mutation here settles, the cache holds server
 *    truth, not a stale optimistic write — so a socket delta arriving after
 *    settlement is reconciling against the real value, not a guess.
 *
 * WHY `unknown` ON `onError`'s AND `onSettled`'s DATA/ERROR PARAMETERS. The
 * installed `@tanstack/react-query` (5.101) types `useMutation` as
 * `useMutation<TData, TError, TVariables, TOnMutateResult>` — its `TData` is
 * what `mutationFn` *resolves to* (here, whatever `api.moveCard` returns),
 * which has nothing to do with `CardListPage`, the shape held at the
 * *affected query keys*. This module never inspects the mutation's result or
 * its error — only `onMutate`'s return value (the snapshot) matters to it —
 * so its `onError`/`onSettled` type those parameters as `unknown` rather than
 * guessing at, or worse forcing, what `TData`/`TError` the real mutation
 * uses. A function parameter typed `unknown` is a supertype of every
 * concrete type in that slot, so `(data: unknown, ...) => void` is
 * assignable wherever `(data: TData | undefined, ...) => unknown` is
 * expected, for *any* `TData` — which is what lets `{ ...optimisticMutation(...) }`
 * spread cleanly into a `useMutation` call whose own `TData`/`TError` this
 * module was never told and does not need.
 */

/**
 * A single query key's cache state, captured before the optimistic write.
 *
 * `existed` is tracked separately from `data` on purpose: a query that is
 * present in the cache with `data: undefined` (for instance, one that is
 * still loading) is a different fact from a key with no cache entry at all,
 * and rollback treats the two differently (see {@link OptimisticMutationHandlers}).
 */
interface QuerySnapshot<TData> {
  readonly key: QueryKey;
  readonly existed: boolean;
  readonly data: TData | undefined;
}

/**
 * The mutation context this module hands `useMutation`. It carries exactly
 * the snapshots rollback needs and nothing else — no `any`, no re-deriving
 * the affected keys from the error or the variables.
 */
export interface OptimisticMutationContext<TData> {
  readonly snapshots: readonly QuerySnapshot<TData>[];
}

/**
 * What a caller supplies to describe one optimistic mutation.
 *
 * @typeParam TVariables - the mutation's variables, e.g. `{ cardId, fromListId, toListId, position }`.
 * @typeParam TData - the shape held at every affected query key. All keys a
 *   single call touches must share this shape (a card move's source and
 *   destination lists are both "a list's card array", so this is not a
 *   practical limit).
 */
export interface OptimisticMutationConfig<TVariables, TData> {
  /** The `QueryClient` this mutation reads and writes. Pass the one from
   * `useQueryClient()` — this module takes it as a parameter rather than
   * importing a singleton so it stays testable against a plain instance. */
  readonly queryClient: QueryClient;
  /**
   * Every query key this mutation's optimistic write affects, computed from
   * the mutation's variables. Returning more than one is the expected case,
   * not an edge case — see the source/destination list example above.
   */
  readonly keys: (variables: TVariables) => readonly QueryKey[];
  /**
   * Produces the optimistic value for one key. Called once per key returned
   * by `keys`, with that key passed back in as the third argument so a
   * caller with several keys in flight can branch on which one it is
   * currently updating (e.g. "remove the card" for the source list's key,
   * "insert the card" for the destination's).
   *
   * Returning `undefined` is a legitimate answer (there is deliberately no
   * optimistic value for this key) and is handled correctly on rollback —
   * see {@link QuerySnapshot}.
   */
  readonly updater: (
    previous: TData | undefined,
    variables: TVariables,
    key: QueryKey,
  ) => TData | undefined;
}

/**
 * The three `useMutation` lifecycle handlers this module produces. Spread
 * the result into `useMutation`'s options alongside `mutationFn` (and
 * `onSuccess`, if the caller needs one — this module does not claim that
 * hook):
 *
 * ```ts
 * useMutation({
 *   mutationFn: moveCard,
 *   ...optimisticMutation({ queryClient, keys, updater }),
 * });
 * ```
 *
 * `onMutate`'s return type is what TypeScript uses to infer the mutation's
 * fourth generic (`TOnMutateResult` in the installed `@tanstack/react-query`)
 * — a caller spreading this in never has to name
 * `OptimisticMutationContext` themselves. `onError` and `onSettled` type
 * their data/error parameters as `unknown` rather than as this module's own
 * `TData` — see the module doc comment's "WHY `unknown`" section for why
 * that, not positional matching, is what makes the spread type-check
 * regardless of what the real mutation resolves to or throws.
 */
export interface OptimisticMutationHandlers<TVariables, TData> {
  onMutate: (variables: TVariables) => Promise<OptimisticMutationContext<TData>>;
  onError: (
    error: unknown,
    variables: TVariables,
    onMutateResult: OptimisticMutationContext<TData> | undefined,
  ) => void;
  onSettled: (
    data: unknown,
    error: unknown,
    variables: TVariables,
    onMutateResult: OptimisticMutationContext<TData> | undefined,
  ) => void;
}

/**
 * Builds the optimistic-update-and-rollback handlers for one mutation.
 */
export function optimisticMutation<TVariables, TData>(
  config: OptimisticMutationConfig<TVariables, TData>,
): OptimisticMutationHandlers<TVariables, TData> {
  const { queryClient, keys, updater } = config;

  return {
    onMutate: async (variables) => {
      const affectedKeys = keys(variables);

      // Cancel first: an in-flight fetch that resolves after our optimistic
      // write, but before rollback or the final invalidation, would otherwise
      // overwrite the optimistic value with data that is already out of date
      // the moment this mutation started.
      await Promise.all(
        affectedKeys.map((key) => queryClient.cancelQueries({ queryKey: key })),
      );

      const snapshots: QuerySnapshot<TData>[] = affectedKeys.map((key) => ({
        key,
        // Exact match: a card-list key and a board-summary key that merely
        // share a prefix are different cache entries, and existence is
        // decided per exact key, not per prefix.
        existed:
          queryClient.getQueryCache().find({ queryKey: key, exact: true }) !== undefined,
        data: queryClient.getQueryData<TData>(key),
      }));

      for (const key of affectedKeys) {
        queryClient.setQueryData<TData>(key, (previous) =>
          updater(previous, variables, key),
        );
      }

      return { snapshots };
    },

    onError: (_error, _variables, onMutateResult) => {
      if (!onMutateResult) return;

      for (const snapshot of onMutateResult.snapshots) {
        if (snapshot.existed) {
          queryClient.setQueryData(snapshot.key, snapshot.data);
        } else {
          // `setQueryData(key, undefined)` would no-op and leave the
          // optimistic value in place (see the module doc comment) — the
          // entry has to be removed outright to genuinely revert to "no
          // data here", which is what the pre-mutation cache actually held.
          queryClient.removeQueries({ queryKey: snapshot.key, exact: true });
        }
      }
    },

    onSettled: (_data, _error, variables) => {
      // Runs on both success and failure. On success this is what turns the
      // optimistic value into the server's own answer; on failure it is a
      // second line of defence behind onError's rollback, in case the cache
      // drifted for a reason rollback didn't anticipate (a socket delta
      // landing mid-mutation, for instance).
      for (const key of keys(variables)) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  };
}
